import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router';
import {
  INPUT_TYPE_LABEL,
  ITEMS_RECEIVED_LABEL,
  ORIGIN_LABEL,
  SPAN_KIND_LABEL,
  SPEC_SCALE_LABEL,
  groupToothItems,
  redoOriginalInSystem,
  sortTeeth,
  type ItemReceivedKey,
} from '@/domain';
import { usePrintPayload } from '@/application/usecases/useIntake';
import type { PrintPayload } from '@/application/ports/PrintRepository';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Odontogram } from '@/ui/data/Odontogram';
import { QrCode } from './QrCode';
import './printSheet.css';
import { PageLoading } from '@/ui/feedback/Loading';

/**
 * Non-breaking, on purpose. The checkbox must never wrap away from its label
 * and the gaps between items must not collapse. Written as an escape so it is
 * visible in review.
 */
const NBSP = ' ';
const BOX_TICKED = '■';
const BOX_EMPTY = '□';

/**
 * How the sheet makes room for long instructions. Section 5 of the request of
 * 5 October 2026: the instructions have an area of their own, wrap onto as many
 * lines as they need and are never cut off, and the sheet still fits one page
 * with nothing over the chart, the QR, the number, the flags or the names.
 *
 * They used to be cut at 190 characters. Now the whole text is printed, and
 * when it does not fit the area at full size the sheet gives in steps, the
 * least costly first: the text a little smaller, then the chart a little
 * smaller, and so on down to the smallest text a bench can read and a chart
 * that still shows every tooth. Each step is the type size of the instructions
 * in millimetres and the scale of the chart. The sheet is measured after it is
 * drawn and takes the first step at which nothing overflows, so short
 * instructions print exactly as they always have.
 */
export const INSTRUCTION_FIT_STEPS: ReadonlyArray<{ font: number; chart: number }> = [
  { font: 3.4, chart: 1 },
  { font: 3.0, chart: 1 },
  { font: 3.0, chart: 0.8 },
  { font: 2.7, chart: 0.8 },
  { font: 2.7, chart: 0.65 },
  { font: 2.4, chart: 0.65 },
  { font: 2.4, chart: 0.55 },
  { font: 2.1, chart: 0.55 },
];

/** A box is overflowing when what it holds is taller than it is, by more than a rounding. */
function overflows(element: HTMLElement | null): boolean {
  return element !== null && element.scrollHeight > element.clientHeight + 1;
}

/**
 * The step the sheet is drawn at: the first at which neither the instructions
 * nor the data block they sit in overflows. Measured after each draw and moved
 * one step at a time, before the browser paints, so the reader never sees the
 * steps in between. It starts again from full size whenever the text changes.
 */
function useInstructionFit(text: string) {
  const dataRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ text, step: 0, more: false });
  const step = fit.text === text ? fit.step : 0;
  const more = fit.text === text && fit.more;

  useLayoutEffect(() => {
    const over = overflows(boxRef.current) || overflows(dataRef.current);
    if (step < INSTRUCTION_FIT_STEPS.length - 1) {
      if (over) setFit({ text, step: step + 1, more: false });
      return;
    }
    /*
     * The last step, and it still does not fit: a text longer than one page
     * can hold at a size a person can read. The page is not allowed to grow,
     * so the sheet says, on the sheet, that the rest is in the system, rather
     * than ending mid-sentence as if that were all of it.
     */
    if (over !== more) setFit({ text, step, more: over });
  }, [text, step, more]);

  return {
    dataRef,
    boxRef,
    step: INSTRUCTION_FIT_STEPS[step] ?? INSTRUCTION_FIT_STEPS[0]!,
    more,
  };
}

/**
 * The A4 job sheet, as approved on 10 September 2026: sections on top, the
 * chart at the foot, the shade boxed under the chart, no sign-off block, and
 * the chart printed without its type names.
 *
 * Read `printSheet.css` before changing anything here: the one page guarantee
 * is structural, not a matter of keeping the content short, and this component
 * is the other half of it.
 */
export function PrintSheetScreen() {
  const { kitId } = useParams<{ kitId: string }>();
  const { data, isPending, isError } = usePrintPayload(kitId);

  if (isPending) return <PageLoading label={strings.print.preparing} />;
  if (isError || data === undefined) return <p>{strings.errors.server}</p>;

  return (
    <div id="print-root">
      <div className="ps-screen-actions" style={{ display: 'flex', gap: 9, marginBottom: 14 }}>
        <Button variant="primary" onClick={() => window.print()}>
          {strings.print.printAction}
        </Button>
        <Button onClick={() => window.history.back()}>{strings.print.close}</Button>
      </div>

      <PrintSheet payload={data} />
    </div>
  );
}

export function PrintSheet({ payload }: { payload: PrintPayload }) {
  const { kit, jobOrder, lab, qr } = payload;
  const teeth = sortTeeth(kit.teeth);

  /*
   * The items on the case, one pair of rows each: the item and its teeth.
   *
   * A mixed case names the teeth against each item, because a bench handed
   * only the case's headline item would make Zircon crowns on the two teeth
   * that were meant to be E.max veneers. `kit.product` is the fallback for a
   * case with an empty chart, which is a case taken over the phone on a unit
   * count with the teeth to follow.
   */
  const itemGroups = groupToothItems(kit.toothItems);

  // The five boxes. The write-in line that followed them went with the request
  // of 13 September 2026, section 3; its text lives in the instructions now.
  const received = (Object.keys(ITEMS_RECEIVED_LABEL) as ItemReceivedKey[]).map(
    (key) => `${kit.itemsReceived[key] ? BOX_TICKED : BOX_EMPTY}${NBSP}${ITEMS_RECEIVED_LABEL[key]}`,
  );

  // Empty when absent, although the type says string: the production client
  // returns responses unvalidated. The incident of 23 September 2026.
  const instructions = kit.instructions ?? '';
  /*
   * A remake of a case from before this system: its original is an old number
   * somebody typed, and the sheet says so in the words every screen uses. The
   * request of 6 October 2026 (second), item 1. Asked of the domain's rule,
   * which reads a sheet cached from an earlier build, with no such field, as a
   * case of this system.
   */
  const legacyOriginal =
    jobOrder.redoOf !== null && !redoOriginalInSystem(jobOrder)
      ? strings.redo.original(jobOrder.redoOf, true)
      : null;
  const { dataRef, boxRef, step, more } = useInstructionFit(
    `${instructions}\n${jobOrder.redoReason ?? ''}\n${legacyOriginal ?? ''}`,
  );
  const fitStyle = {
    '--ps-instr-font': `${String(step.font)}mm`,
    '--ps-chart-scale': String(step.chart),
  } as CSSProperties;

  return (
    <div className="ps" style={fitStyle} data-instr-font={step.font} data-chart-scale={step.chart}>
      {/* the header: letterhead on the left, the case number and QR on the right */}
      <div className="ps-head">
        <div className="ps-head-left">
          <div className="ps-brand">{lab.name}</div>
          <div className="ps-sub">{strings.print.sheetTitle}</div>
          {/* The name, and nothing under it. The address, telephone number and
              email address came off the sheet with the request of
              13 September 2026, section 5, and the approved Figma sheet's
              "Dubai, United Arab Emirates" line went with them. */}
        </div>
        <div className="ps-head-right">
          <div className="ps-jo">{kit.jobOrderNo}</div>
          {/*
            The flags, directly under the number and beside the QR, where the
            sheet is read on the tray. URGENT moved here from under the lab's
            name with the request of 3 October 2026, section 1. A remake that
            reaches the bench without saying it is a remake is the failure the
            redo exists to prevent, so it says so on the same line. Neither
            flag leaves a gap when it is absent: the row is not drawn at all.
          */}
          {(kit.urgent || jobOrder.redoOf !== null) && (
            <div className="ps-flags">
              {kit.urgent && <span className="ps-urg">{strings.print.urgent}</span>}
              {jobOrder.redoOf !== null && (
                <span className="ps-redo">
                  {strings.print.redoOf} {jobOrder.redoOf}
                </span>
              )}
            </div>
          )}
          <div className="ps-kit">
            {kit.id} · {kit.qrCode}
          </div>
        </div>
        {/*
          Folded twice, the top right corner still faces up on the tray, which
          is why the number and the code sit here. The quiet zone is inside the
          SVG, drawn by QrCode.tsx.
        */}
        <div className="ps-qr">
          <QrCode value={qr.value} />
        </div>
      </div>
      <hr className="ps-rule" />

      {/*
        The data block hugs its content and grows downward; the chart is
        placed from the foot. The instructions box takes whatever is left
        between the two, and when its text needs more the sheet makes room: see
        INSTRUCTION_FIT_STEPS. The block never reaches the chart.
      */}
      <div className="ps-data" ref={dataRef}>
        {/*
          Who the case is for, in a band of its own across the top of the data:
          the dentist and the patient side by side, each label over its name,
          each name across half the page and wrapping as long as it needs to.
          Request of 4 October 2026 (second), section 10. In the first column a
          long name had a third of the page and took five lines; here it takes
          two, and the instructions below keep their room.
        */}
        {(kit.dentistName != null || kit.patientName != null) && (
          <div className="ps-people">
            {kit.dentistName != null && <Row label={strings.print.dentist} value={kit.dentistName} name />}
            {kit.patientName != null && <Row label={strings.print.patient} value={kit.patientName} name />}
          </div>
        )}
        <div className="ps-cols three">
          <div className="ps-col">
            <div className="ps-sec">{strings.print.case}</div>
            {/*
              The identity rows follow the payload. A technician printing a
              sheet receives no dentist or patient name, so the sheet they
              print carries none either.
            */}
            <Row label={strings.print.origin} value={ORIGIN_LABEL[kit.origin]} />
            {/* The day the case reached the lab, in lab time. A case entered
                without one falls back to its start date. */}
            <Row
              label={strings.print.received}
              value={kit.sentAt === null ? kit.startDate : formatDay(kit.sentAt)}
              mono
            />
            <Row label={strings.print.startDate} value={kit.startDate} mono />
            {/* Bold and red, always, whatever the date: it is the one value on
                the sheet that decides what a technician does next, and the only
                other ink on the page is the urgent flag. Section 4. */}
            <Row label={strings.print.deliveryDate} value={kit.deliveryDate} mono strong />
          </div>

          <div className="ps-col">
            <div className="ps-sec">{strings.print.restoration}</div>
            {/* ITEM n and TEETH for each distinct item, as the approved sheet has it. */}
            {itemGroups.length === 0 ? (
              <Row label={`${strings.print.item} 1`} value={kit.product} wrap />
            ) : (
              itemGroups.map((group, index) => (
                <div key={group.product}>
                  <Row
                    label={`${strings.print.item} ${String(index + 1)}`}
                    value={group.product}
                    wrap
                  />
                  <Row label={strings.print.teeth} value={group.teeth.join(', ')} mono wrap />
                </div>
              ))
            )}
            {/* The kit's own count, not teeth.length: a case opened on a count
                alone has units and an empty chart. */}
            <Row label={strings.print.units} value={String(kit.units)} mono />
            <Row label={strings.print.input} value={INPUT_TYPE_LABEL[kit.input]} />
          </div>

          <div className="ps-col">
            <div className="ps-sec">{strings.print.specification}</div>
            <Row label={strings.print.ingot} value={kit.specification.ingot} />
            <Row label={strings.print.block} value={kit.specification.block} />
            <Row
              label={strings.print.translucency}
              value={SPEC_SCALE_LABEL[kit.specification.translucency]}
            />
            <Row label={strings.print.anatomy} value={SPEC_SCALE_LABEL[kit.specification.anatomy]} />
            <Row label={strings.print.incisal} value={SPEC_SCALE_LABEL[kit.specification.incisal]} />
            <Row label={strings.print.occlusal} value={SPEC_SCALE_LABEL[kit.specification.occlusal]} />
            <Row label={strings.print.stain} value={kit.specification.stain} />
          </div>
        </div>

        <div className="ps-cols two">
          <div className="ps-col">
            <div className="ps-sec">{strings.print.itemsReceived}</div>
            {/* Two lines, no item split across a line: each item is one
                non-breaking token and the line wraps between them. */}
            <div className="ps-items">
              {received.map((item) => (
                <span key={item} className="ps-item">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="ps-col">
            {jobOrder.spans.length > 0 && (
              <>
                <div className="ps-sec">{strings.print.bridges}</div>
                {jobOrder.spans.map((span) => (
                  <Row
                    key={span.id}
                    label={span.name}
                    value={`${SPAN_KIND_LABEL[span.kind]}, ${span.from} ${strings.odontogram.to} ${span.to}`}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {/*
          The instructions, in an area of their own: the one field, in full, as
          typed, wrapping onto as many lines as it needs. Section 5 of the
          request of 5 October 2026.
        */}
        <div className="ps-sec">{strings.print.instructions}</div>
        <div className="ps-instr" ref={boxRef} data-testid="print-instructions">
          {/*
            An original from before this system, in full and first. The flag
            beside the QR has room for a number and no more, and a number typed
            by hand may be long; in this box it wraps like any other line.
          */}
          {legacyOriginal !== null && <div className="ps-redo-reason">{legacyOriginal}</div>}
          {/* Why it came back, first, before anything else the reader sees. */}
          {jobOrder.redoOf !== null && jobOrder.redoReason !== null && (
            <div className="ps-redo-reason">
              {strings.print.redoReason}: {jobOrder.redoReason}
            </div>
          )}
          {instructions}
          {more && (
            <div className="ps-instr-more" data-testid="print-instructions-more">
              {strings.print.continued}
            </div>
          )}
        </div>
      </div>

      {/* the chart, at the foot, placed from the bottom */}
      <div className="ps-chart">
        <div className="ps-sec">{strings.print.teethFdi}</div>
        <div className="ps-teeth">
          <Odontogram selected={teeth} readOnly size="print" spans={jobOrder.spans} />
        </div>
      </div>

      {/* The shade is the first thing a ceramist reads with the tray in hand. */}
      <div className="ps-shade">
        <div className="ps-shade-label">{strings.print.shade}</div>
        <div className={`ps-shade-value ${shadeSize(kit.shade)}`}>{kit.shade}</div>
      </div>
    </div>
  );
}

/** How large the shade can print in its box: as large as its length allows. */
export function shadeSize(shade: string): '' | 'mid' | 'long' {
  if (shade.length <= 8) return '';
  return shade.length <= 22 ? 'mid' : 'long';
}

function Row({
  label,
  value,
  mono = false,
  wrap = false,
  name = false,
  strong = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wrap?: boolean;
  /** An identity row: the label above, the name below across the column, wrapping, never cut. */
  name?: boolean;
  /** The delivery date: bold and red, the one coloured value on the page besides the urgent flag. */
  strong?: boolean;
}) {
  const classes = ['v'];
  if (mono) classes.push('mono');
  if (wrap) classes.push('wrap');
  if (name) classes.push('name');
  if (strong) classes.push('strong');

  /*
   * A dentist or a patient: the label on its own line and the name under it,
   * across the whole column, wrapping as long as it needs to. Request of
   * 4 October 2026 (second), section 10: beside the label, a long name had
   * only what the label left and was squeezed or cut.
   */
  if (name) {
    return (
      <div className="ps-row stack">
        <span className="k">{label}</span>
        {/* Its own direction, so an Arabic name reads and aligns right to left. */}
        <span className="v name" dir="auto">
          {value}
        </span>
      </div>
    );
  }

  return (
    <div className="ps-row">
      <span className="k">{label}</span>
      <span className={classes.join(' ')}>{value}</span>
    </div>
  );
}
